import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Card, useToast } from '@caruso/ui';
import { venueReservationsService } from '@/services/venue-reservations.service';
import {
  ArrowLeft,
  Calendar,
  Clock,
  User,
  Mail,
  Phone,
  MapPin,
  DollarSign,
  CheckCircle,
  XCircle,
  AlertCircle,
  FileText,
  Edit,
  Trash2,
} from 'lucide-react';
import { ReservationStatus } from '@caruso/types';
import { cn } from '@caruso/ui';

export function VenueReservationDetailsPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [confirmingAction, setConfirmingAction] = useState<'confirm' | 'cancel' | 'delete' | null>(null);

  const { data: reservation, isLoading } = useQuery({
    queryKey: ['venue-reservation', id],
    queryFn: () => venueReservationsService.getById(id!),
    enabled: !!id,
  });

  const updateMutation = useMutation({
    mutationFn: (data: { status: ReservationStatus }) =>
      venueReservationsService.update(id!, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['venue-reservation', id] });
      queryClient.invalidateQueries({ queryKey: ['calendar-reservations'] });
      toast({
        title: 'Reserva atualizada',
        variant: 'success',
      });
      setConfirmingAction(null);
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao atualizar reserva',
        description: error?.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => venueReservationsService.delete(id!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['calendar-reservations'] });
      toast({
        title: 'Reserva removida',
        variant: 'success',
      });
      navigate('/app/venue-reservations');
    },
    onError: (error: any) => {
      toast({
        title: 'Erro ao remover reserva',
        description: error?.response?.data?.message || 'Tente novamente',
        variant: 'destructive',
      });
    },
  });

  const handleConfirm = () => {
    updateMutation.mutate({ status: 'confirmed' as ReservationStatus });
  };

  const handleCancel = () => {
    updateMutation.mutate({ status: 'cancelled' as ReservationStatus });
  };

  const handleDelete = () => {
    deleteMutation.mutate();
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(price);
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatPhone = (phone?: string) => {
    if (!phone) return '';
    // Remove all non-digits
    const digits = phone.replace(/\D/g, '');

    // Brazilian numbers
    if (digits.startsWith('55') && digits.length >= 12) {
      // +55 (XX) XXXXX-XXXX or +55 (XX) XXXX-XXXX
      const ddd = digits.slice(2, 4);
      const rest = digits.slice(4);
      if (rest.length === 9) {
        return `+55 (${ddd}) ${rest.slice(0, 5)}-${rest.slice(5)}`;
      } else if (rest.length === 8) {
        return `+55 (${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
      }
    }

    // Brazilian without country code
    if (digits.length === 11) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 7)}-${digits.slice(7)}`;
    }
    if (digits.length === 10) {
      return `(${digits.slice(0, 2)}) ${digits.slice(2, 6)}-${digits.slice(6)}`;
    }

    // International format
    if (digits.length > 10) {
      return `+${digits.slice(0, digits.length - 10)} ${digits.slice(-10, -6)} ${digits.slice(-6)}`;
    }

    return phone;
  };

  const getStatusConfig = (status: ReservationStatus) => {
    const configs = {
      pending: {
        label: 'Pendente',
        color: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
        icon: AlertCircle,
      },
      confirmed: {
        label: 'Confirmada',
        color: 'bg-green-500/20 text-green-400 border-green-500/30',
        icon: CheckCircle,
      },
      cancelled: {
        label: 'Cancelada',
        color: 'bg-red-500/20 text-red-400 border-red-500/30',
        icon: XCircle,
      },
      completed: {
        label: 'Concluída',
        color: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
        icon: CheckCircle,
      },
    };
    return configs[status] || configs.pending;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin w-12 h-12 border-4 border-brand-gold border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="text-center py-12">
        <h1 className="text-2xl text-brand-gold mb-4">Reserva não encontrada</h1>
        <Button onClick={() => navigate('/app/venue-reservations')}>Voltar para listagem</Button>
      </div>
    );
  }

  const statusConfig = getStatusConfig(reservation.status);
  const StatusIcon = statusConfig.icon;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-brand-gold">Detalhes da Reserva</h1>
            <p className="text-brand-ivory/60 mt-1">
              {reservation.venue?.name}
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => navigate(`/app/venue-reservations/${id}/edit`)}
          >
            <Edit className="w-4 h-4 mr-2" />
            Editar
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Main Info */}
        <div className="lg:col-span-2 space-y-6">
          {/* Status Card */}
          <Card className="bg-brand-ink/50 border-brand-gold/20 p-6">
            <div className="flex items-center justify-between">
              <div className={cn('flex items-center gap-3 px-4 py-2 rounded-lg border', statusConfig.color)}>
                <StatusIcon className="w-5 h-5" />
                <span className="font-semibold">{statusConfig.label}</span>
              </div>

              {/* Action Buttons */}
              {reservation.status === 'pending' && (
                <div className="flex gap-2">
                  {confirmingAction === 'confirm' ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-brand-ivory/60">Confirmar reserva?</span>
                      <Button
                        size="sm"
                        variant="premium"
                        onClick={handleConfirm}
                        disabled={updateMutation.isPending}
                      >
                        Sim
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmingAction(null)}
                      >
                        Não
                      </Button>
                    </div>
                  ) : confirmingAction === 'cancel' ? (
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-brand-ivory/60">Cancelar reserva?</span>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={handleCancel}
                        disabled={updateMutation.isPending}
                      >
                        Sim
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setConfirmingAction(null)}
                      >
                        Não
                      </Button>
                    </div>
                  ) : (
                    <>
                      <Button
                        variant="premium"
                        onClick={() => setConfirmingAction('confirm')}
                      >
                        <CheckCircle className="w-4 h-4 mr-2" />
                        Confirmar
                      </Button>
                      <Button
                        variant="outline"
                        className="text-red-400 hover:text-red-300 hover:border-red-400"
                        onClick={() => setConfirmingAction('cancel')}
                      >
                        <XCircle className="w-4 h-4 mr-2" />
                        Cancelar
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>
          </Card>

          {/* Date & Time */}
          <Card className="bg-brand-ink/50 border-brand-gold/20 p-6">
            <h3 className="text-lg font-semibold text-brand-gold mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              Data e Horário
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-brand-ivory/60 mb-1">Data Início</p>
                <p className="text-brand-ivory font-medium">{formatDate(reservation.startDate)}</p>
              </div>
              <div>
                <p className="text-sm text-brand-ivory/60 mb-1">Data Fim</p>
                <p className="text-brand-ivory font-medium">{formatDate(reservation.endDate)}</p>
              </div>
              <div>
                <p className="text-sm text-brand-ivory/60 mb-1">Hora Início</p>
                <p className="text-brand-ivory font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4 text-brand-gold" />
                  {reservation.startTime}
                </p>
              </div>
              <div>
                <p className="text-sm text-brand-ivory/60 mb-1">Hora Fim</p>
                <p className="text-brand-ivory font-medium flex items-center gap-2">
                  <Clock className="w-4 h-4 text-brand-gold" />
                  {reservation.endTime}
                </p>
              </div>
            </div>
          </Card>

          {/* Customer Info */}
          <Card className="bg-brand-ink/50 border-brand-gold/20 p-6">
            <h3 className="text-lg font-semibold text-brand-gold mb-4 flex items-center gap-2">
              <User className="w-5 h-5" />
              Dados do Cliente
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <User className="w-4 h-4 text-brand-ivory/50" />
                {reservation.clientId ? (
                  <a
                    href={`/app/clientes/${reservation.clientId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-gold hover:underline cursor-pointer"
                  >
                    {reservation.customerName}
                  </a>
                ) : (
                  <span className="text-brand-ivory">{reservation.customerName}</span>
                )}
              </div>
              {reservation.customerEmail && (
                <div className="flex items-center gap-3">
                  <Mail className="w-4 h-4 text-brand-ivory/50" />
                  <a
                    href={`mailto:${reservation.customerEmail}`}
                    className="text-brand-gold hover:underline"
                  >
                    {reservation.customerEmail}
                  </a>
                </div>
              )}
              {reservation.customerPhone && (
                <div className="flex items-center gap-3">
                  <Phone className="w-4 h-4 text-brand-ivory/50" />
                  <a
                    href={`https://wa.me/${reservation.customerPhone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-gold hover:underline"
                  >
                    {formatPhone(reservation.customerPhone)}
                  </a>
                </div>
              )}
            </div>
          </Card>

          {/* Notes */}
          {reservation.notes && (
            <Card className="bg-brand-ink/50 border-brand-gold/20 p-6">
              <h3 className="text-lg font-semibold text-brand-gold mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5" />
                Observações
              </h3>
              <p className="text-brand-ivory/80 whitespace-pre-wrap">{reservation.notes}</p>
            </Card>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {/* Venue Info */}
          <Card className="bg-brand-ink/50 border-brand-gold/20 p-6">
            <h3 className="text-lg font-semibold text-brand-gold mb-4 flex items-center gap-2">
              <MapPin className="w-5 h-5" />
              Ambiente
            </h3>
            {reservation.venue && (
              <div className="space-y-3">
                <p className="text-brand-ivory font-medium text-lg">{reservation.venue.name}</p>
                {reservation.venue.capacity && (
                  <p className="text-sm text-brand-ivory/60">
                    Capacidade: {reservation.venue.capacity} pessoas
                  </p>
                )}
              </div>
            )}
          </Card>

          {/* Price Info */}
          <Card className="bg-brand-ink/50 border-brand-gold/20 p-6">
            <h3 className="text-lg font-semibold text-brand-gold mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              Valores
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between">
                <span className="text-brand-ivory/60">Subtotal</span>
                <span className="text-brand-ivory">{formatPrice(reservation.totalPrice || 0)}</span>
              </div>
              {(reservation.discount || 0) > 0 && (
                <div className="flex justify-between text-green-400">
                  <span>Desconto</span>
                  <span>-{formatPrice(reservation.discount || 0)}</span>
                </div>
              )}
              <div className="flex justify-between pt-3 border-t border-brand-gold/20">
                <span className="text-brand-gold font-semibold">Total</span>
                <span className="text-brand-gold font-bold text-xl">
                  {formatPrice(reservation.finalPrice || reservation.totalPrice || 0)}
                </span>
              </div>
            </div>
          </Card>

          {/* Danger Zone */}
          <Card className="bg-red-500/5 border-red-500/20 p-6">
            <h3 className="text-lg font-semibold text-red-400 mb-4">Zona de Perigo</h3>
            {confirmingAction === 'delete' ? (
              <div className="space-y-3">
                <p className="text-sm text-brand-ivory/60">
                  Tem certeza que deseja excluir esta reserva? Esta ação não pode ser desfeita.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant="destructive"
                    className="flex-1"
                    onClick={handleDelete}
                    disabled={deleteMutation.isPending}
                  >
                    {deleteMutation.isPending ? 'Excluindo...' : 'Sim, excluir'}
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    onClick={() => setConfirmingAction(null)}
                  >
                    Cancelar
                  </Button>
                </div>
              </div>
            ) : (
              <Button
                variant="outline"
                className="w-full text-red-400 hover:text-red-300 hover:border-red-400"
                onClick={() => setConfirmingAction('delete')}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Excluir Reserva
              </Button>
            )}
          </Card>
        </div>
      </div>
    </div>
  );
}
